import { useEffect, useState } from 'react';
import { Button, Typography } from '@navi/web-ui/lib/primitives';
import Cardstyles from './AddressIngestion.module.scss';
import { GenericObject } from '@cp/src/types/CommonConstans';
import { toast } from '@navi/web-ui/lib/primitives/Toast/core/toast';
import styles from '../AllocationServices/BulkUpload/index.module.scss';
import UploadHistoryIcon from '@cp/src/assets/icons/UploadHistoryIcon';
import { useColumnDefs } from './ColumnDefs';
import { UseUpload } from '@cp/src/components/UploadComponent';
import axiosInstance, { ApiKeys, getApiUrl, logError } from '@cp/src/utils/ApiHelper';
import FileUploadContainerV2 from '@cp/src/components/UploadComponent/FileUploadContainerV2';
import UploadHistoryDrawer from '@cp/src/components/UploadComponent/UploadHistoryDrawer';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '@cp/src/store';
import { fetchUploadHistory, submitAddressIngestionFile } from './actions/AddressIngestionActions';

const AddressIngestion: React.FC = () => {
  const [showLoadingComponent, setShowLoadingComponent] = useState(false);
  const uploadHistory = useSelector((state: RootState) => state.uploadAddressHistory);
  const [showUploadHistory, setShowUploadHistory] = useState(false);
  const { columnDefs } = useColumnDefs();
  const [referenceId, setReferenceId] = useState(null);
  const dispatch = useDispatch();

  const onFileUploadedError = () => {
    setError({
      message: 'An error occurred while uploading the file',
      fileName: file?.name || ''
    });
    toast.error('Failed to upload the File');
  };
  const onFileUploadedSuccess = async (res?: GenericObject) => {
    const ackUrl = getApiUrl(ApiKeys.ADDRESS_INGESTION_UPLOAD_ACK, {}, { entityId: res?.data?.id });
    try {
      setShowLoadingComponent(true);
      const response = await axiosInstance.get(ackUrl);
      if (response?.data?.valid === false) {
        const errorMessage =
          response?.data?.message || 'File upload failed. Please verify the data entries';
        setError({
          message: errorMessage,
          fileName: file?.name || ''
        });
        toast.error(errorMessage);
      } else {
        setError({ message: '', fileName: '' });
      }
    } catch (err) {
      setError({
        message: 'An error occurred while uploading the file.',
        fileName: file?.name || ''
      });
      toast.error('Failed to upload the File');
    } finally {
      setShowLoadingComponent(false);
    }
    setReferenceId(res?.data?.id);
  };

  const { file, setFile, upload, error, setError } = UseUpload({
    onFileUploadedSuccess: onFileUploadedSuccess,
    onFileUploadedError: onFileUploadedError
  });

  const handleSubmitButtonClick = async () => {
    if (referenceId !== null) {
      setShowLoadingComponent(true);
      try {
        await submitAddressIngestionFile(referenceId);
        setReferenceId(null);
        setFile(null);
      } catch (error) {
        toast.error('Failed to upload the File');
      } finally {
        setShowLoadingComponent(false);
      }
    }
  };

  const handleUploadHistoryClick = () => {
    setShowUploadHistory(true);
  };

  useEffect(() => {
    if (file) {
      setShowLoadingComponent(true);
      const getUploadUrl = getApiUrl(
        ApiKeys.ADDRESS_INGESTION_UPLOAD_URL,
        {},
        {
          originalFileName: file?.name?.replace(/\.csv$/i, '') || ''
        }
      );
      axiosInstance.get(getUploadUrl).then(async res => {
        try {
          await upload(file, res?.data?.presignedUrl, res);
        } catch {
          setError({
            message: 'File upload failed. Please verify the data entries',
            fileName: file?.name || ''
          });
        }
      });
    }
  }, [file]);

  const getUploadHistory = () => {
    dispatch(fetchUploadHistory());
  };

  return (
    <div className={Cardstyles.container}>
      <div className={Cardstyles.topContainer}>
        <Typography variant={'h2'}>Upload addresses</Typography>
        <Button
          variant="text"
          size="medium"
          startAdornment={<UploadHistoryIcon />}
          onClick={handleUploadHistoryClick}
        >
          <Typography color="var(--blue-base)" variant={'p5'}>
            View Upload History
          </Typography>
        </Button>
      </div>
      <div className={Cardstyles.card}>
        <Typography className="p-3" variant={'h3'}>
          Select the file
        </Typography>
        <FileUploadContainerV2
          error={error}
          file={file}
          setFile={setFile}
          setError={setError}
          showLoadingComponent={showLoadingComponent}
          setShowLoadingComponent={setShowLoadingComponent}
          styles={styles}
          isSampleFileAvailable={false}
          supportedFile=".csv"
        />
        <div className="flex justify-end mt-4">
          <Button
            disabled={!file || showLoadingComponent || !!error?.message}
            onClick={handleSubmitButtonClick}
            type="submit"
            style={{ width: '200px' }}
          >
            Submit
          </Button>
        </div>
      </div>
      <UploadHistoryDrawer
        showUploadHistoryComponent={showUploadHistory}
        setShowUploadHistoryComponent={setShowUploadHistory}
        data={uploadHistory?.data || []}
        columnDefs={columnDefs}
        fetchUploadHistory={getUploadHistory}
        headerText="Address Ingestion History"
        typeOfUpload="ADDRESS_INGESTION"
        showPagination={false}
      />
    </div>
  );
};

export default AddressIngestion;
